import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../../components/SEO';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import type { SnackbarSeverity } from '../../components/common/NotificationSnackbar';
import { hostApiClient } from '../../hostApiClient';
import {
  GoogleCalendarIntegration,
  GoogleCalendarIntegrationCalendar,
  GoogleCalendarIntegrationStatus,
  GoogleCalendarOption,
} from '../../models/GoogleCalendar';

const STATUS_SORT_ORDER: Record<GoogleCalendarIntegrationStatus, number> = {
  BROKEN: 0,
  STALE: 1,
  NOT_SYNCED: 2,
  ACTIVE: 3,
  DISCONNECTED: 4,
};

function formatLastSyncedAt(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return format(date, 'MMM d, yyyy h:mm a');
  } catch {
    return '—';
  }
}

function statusChipColor(
  status: GoogleCalendarIntegrationStatus,
): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'STALE':
    case 'NOT_SYNCED':
      return 'warning';
    case 'BROKEN':
      return 'error';
    case 'DISCONNECTED':
      return 'default';
    default:
      return 'info';
  }
}

function rowKey(calendar: GoogleCalendarIntegrationCalendar): string {
  return `${calendar.scope}:${calendar.entityId}`;
}

const HostGoogleCalendarPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [integration, setIntegration] = useState<GoogleCalendarIntegration | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scopesError, setScopesError] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [calendarOptions, setCalendarOptions] = useState<GoogleCalendarOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [savingRowKey, setSavingRowKey] = useState<string | null>(null);

  const fetchIntegration = useCallback(async (opts?: { quiet?: boolean }) => {
    if (opts?.quiet) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await hostApiClient.getGoogleCalendarIntegration();
      setIntegration(data);
    } catch {
      setError(
        t(
          'hostGoogleCalendar.fetchError',
          'Failed to load Google Calendar integration status',
        ),
      );
      setIntegration(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  const fetchCalendarOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const options = await hostApiClient.getGoogleCalendarOptions();
      setCalendarOptions(options);
    } catch {
      setCalendarOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchIntegration();
  }, [fetchIntegration]);

  useEffect(() => {
    if (integration?.connected) {
      void fetchCalendarOptions();
    } else {
      setCalendarOptions([]);
    }
  }, [fetchCalendarOptions, integration?.connected]);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam !== 'not_all_scopes_provided') return;

    setScopesError(true);
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    navigate({ search: next.toString() ? `?${next.toString()}` : '' }, { replace: true });
  }, [navigate, searchParams]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const consentLinkResponse = await hostApiClient.getGoogleCalendarConsentLink();
      if (!consentLinkResponse?.googleCalendarConsentUrl) {
        throw new Error('No consent link received from server');
      }
      window.location.href = consentLinkResponse.googleCalendarConsentUrl;
    } catch {
      setSnackbarMessage(
        t('hostGoogleCalendar.connectionError', 'Failed to get Google Calendar consent link'),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await hostApiClient.disconnectGoogleCalendar();
      setDisconnectDialogOpen(false);
      setSnackbarMessage(
        t('hostGoogleCalendar.disconnectSuccess', 'Google Calendar disconnected'),
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await fetchIntegration({ quiet: true });
    } catch {
      setSnackbarMessage(
        t('hostGoogleCalendar.disconnectError', 'Failed to disconnect Google Calendar'),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCalendarLinkChange = async (
    calendar: GoogleCalendarIntegrationCalendar,
    selectedId: string,
  ) => {
    const key = rowKey(calendar);
    const currentId = calendar.googleCalendarId || '';
    if (selectedId === currentId) return;

    const googleCalendarInfo =
      selectedId === ''
        ? null
        : calendarOptions.find((option) => option.id === selectedId) ??
          (calendar.googleCalendarId === selectedId
            ? {
                id: calendar.googleCalendarId,
                name: calendar.googleCalendarName || calendar.googleCalendarId,
              }
            : null);

    if (selectedId !== '' && !googleCalendarInfo) {
      return;
    }

    setSavingRowKey(key);
    try {
      const request = { googleCalendarInfo };
      if (calendar.scope === 'ROOM') {
        await hostApiClient.updateRoomCalendar(calendar.entityId, request);
      } else {
        await hostApiClient.updateTutorCalendar(calendar.entityId, request);
      }
      setSnackbarMessage(
        googleCalendarInfo
          ? t('hostGoogleCalendar.linkSuccess', 'Google Calendar linked')
          : t('hostGoogleCalendar.unlinkSuccess', 'Google Calendar unlinked'),
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await fetchIntegration({ quiet: true });
    } catch {
      setSnackbarMessage(
        t('hostGoogleCalendar.linkError', 'Failed to update Google Calendar link'),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSavingRowKey(null);
    }
  };

  const renderScopeLabel = (calendar: GoogleCalendarIntegrationCalendar) => {
    if (calendar.scope === 'ROOM') {
      return t('hostGoogleCalendar.scopeRoom', 'Room');
    }
    if (calendar.scope === 'TUTOR') {
      return t('hostGoogleCalendar.scopeTutor', 'Tutor');
    }
    return calendar.scope;
  };

  const renderStatusLabel = (status: GoogleCalendarIntegrationStatus) => {
    switch (status) {
      case 'ACTIVE':
        return t('hostGoogleCalendar.status.active', 'Active');
      case 'STALE':
        return t('hostGoogleCalendar.status.stale', 'Stale');
      case 'BROKEN':
        return t('hostGoogleCalendar.status.broken', 'Broken');
      case 'NOT_SYNCED':
        return t('hostGoogleCalendar.status.notSynced', 'Not synced');
      case 'DISCONNECTED':
        return t('hostGoogleCalendar.status.disconnected', 'Disconnected');
      default:
        return status;
    }
  };

  const connected = integration?.connected === true;
  const calendars = integration?.calendars ?? [];

  const sortedCalendars = useMemo(
    () =>
      [...calendars].sort((a, b) => {
        const statusDiff =
          (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99);
        if (statusDiff !== 0) return statusDiff;
        return (a.entityName || '').localeCompare(b.entityName || '');
      }),
    [calendars],
  );

  const healthCounts = useMemo(() => {
    const counts = { active: 0, attention: 0 };
    for (const calendar of calendars) {
      if (calendar.status === 'ACTIVE') counts.active += 1;
      else if (calendar.status !== 'DISCONNECTED') counts.attention += 1;
    }
    return counts;
  }, [calendars]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SEO title={t('hostGoogleCalendar.title', 'Google Calendar')} noindex={true} />
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 960, width: '100%', mx: 'auto' }}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            position: 'relative',
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Tooltip title={t('hostGoogleCalendar.refresh', 'Refresh status')}>
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <IconButton
                size="small"
                onClick={() => {
                  void fetchIntegration({ quiet: true });
                  if (connected) void fetchCalendarOptions();
                }}
                disabled={refreshing || connecting || disconnecting || savingRowKey !== null}
                aria-label={t('hostGoogleCalendar.refresh', 'Refresh status')}
              >
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Tooltip>

          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 3, pr: 5 }}>
            <CalendarMonthIcon color="primary" sx={{ fontSize: 28, mt: 0.35 }} />
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                {t('hostGoogleCalendar.title', 'Google Calendar')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 520 }}>
                {t(
                  'hostGoogleCalendar.subtitle',
                  'Connect a Google account so room and tutor bookings can sync to calendars.',
                )}
              </Typography>
            </Box>
          </Stack>

          {scopesError && (
            <Alert severity="warning" sx={{ mb: 2.5 }} onClose={() => setScopesError(false)}>
              {t(
                'hostGoogleCalendar.scopesError',
                'Not all requested scopes were provided. Please enable all scopes to enable Google Calendar Integration.',
              )}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          {connected ? (
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2.5,
                border: 1,
                borderColor: 'success.dark',
                bgcolor: 'rgba(46, 125, 50, 0.08)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  bgcolor: 'success.main',
                }}
              />
              <Stack spacing={2.5} sx={{ p: { xs: 2, md: 2.5 }, pl: { xs: 2.5, md: 3 } }}>
                <Stack direction="row" alignItems="center" spacing={1.75}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'success.main',
                      color: 'success.contrastText',
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircleIcon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      sx={{ display: 'block', lineHeight: 1.2, color: 'success.main', letterSpacing: '0.12em' }}
                    >
                      {t('hostGoogleCalendar.statusLabel', 'Status')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.light', lineHeight: 1.25 }}>
                      {t('hostGoogleCalendar.connected', 'Connected')}
                    </Typography>
                    {integration?.connectedEmail && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}
                        noWrap
                      >
                        {integration.connectedEmail}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {(healthCounts.active > 0 || healthCounts.attention > 0) && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {healthCounts.active > 0 && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={t('hostGoogleCalendar.summary.active', '{{count}} active', {
                          count: healthCounts.active,
                        })}
                      />
                    )}
                    {healthCounts.attention > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        icon={<WarningAmberIcon />}
                        label={t('hostGoogleCalendar.summary.needsAttention', '{{count}} need attention', {
                          count: healthCounts.attention,
                        })}
                      />
                    )}
                  </Stack>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={disconnecting ? <CircularProgress size={18} /> : <LinkOffIcon />}
                    onClick={() => setDisconnectDialogOpen(true)}
                    disabled={disconnecting || savingRowKey !== null}
                  >
                    {disconnecting
                      ? t('hostGoogleCalendar.disconnecting', 'Disconnecting...')
                      : t('hostGoogleCalendar.disconnect', 'Disconnect')}
                  </Button>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={connecting ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
                onClick={() => void handleConnect()}
                disabled={connecting}
              >
                {connecting
                  ? t('hostGoogleCalendar.connecting', 'Connecting...')
                  : t('hostGoogleCalendar.connect', 'Connect Google Calendar')}
              </Button>
            </Box>
          )}

          {connected && (
            <Box sx={{ mt: 3.5 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5, letterSpacing: '0.1em' }}
              >
                {t('hostGoogleCalendar.calendarsTitle', 'Sync health')}
              </Typography>

              {sortedCalendars.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'hostGoogleCalendar.noCalendarsConnected',
                    'No rooms or tutors to link yet.',
                  )}
                </Typography>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {t('hostGoogleCalendar.column.entity', 'Name')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {t('hostGoogleCalendar.column.scope', 'Type')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 220 }}>
                          {t('hostGoogleCalendar.column.googleCalendar', 'Google Calendar')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {t('hostGoogleCalendar.column.status', 'Status')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {t('hostGoogleCalendar.column.lastSynced', 'Last synced')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedCalendars.map((calendar) => {
                        const key = rowKey(calendar);
                        const isSaving = savingRowKey === key;
                        const needsAttention =
                          calendar.status === 'BROKEN' ||
                          calendar.status === 'STALE' ||
                          calendar.status === 'NOT_SYNCED';
                        const selectedId = calendar.googleCalendarId || '';
                        const hasOrphanSelection =
                          Boolean(selectedId) &&
                          !calendarOptions.some((option) => option.id === selectedId);
                        return (
                          <TableRow
                            key={key}
                            sx={
                              needsAttention
                                ? {
                                    bgcolor: (theme) =>
                                      theme.palette.mode === 'dark'
                                        ? 'rgba(237, 108, 2, 0.08)'
                                        : 'rgba(237, 108, 2, 0.06)',
                                  }
                                : undefined
                            }
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {calendar.entityName || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" variant="outlined" label={renderScopeLabel(calendar)} />
                            </TableCell>
                            <TableCell>
                              {optionsLoading && calendarOptions.length === 0 ? (
                                <CircularProgress size={18} />
                              ) : (
                                <FormControl size="small" fullWidth sx={{ minWidth: 200 }}>
                                  <Select
                                    displayEmpty
                                    value={selectedId}
                                    disabled={
                                      isSaving ||
                                      disconnecting ||
                                      (calendarOptions.length === 0 && !hasOrphanSelection)
                                    }
                                    onChange={(event) => {
                                      void handleCalendarLinkChange(calendar, event.target.value);
                                    }}
                                    renderValue={(value) => {
                                      if (!value) {
                                        return (
                                          <Typography variant="body2" color="text.secondary">
                                            {t(
                                              'hostGoogleCalendar.noCalendarSelected',
                                              'No calendar selected',
                                            )}
                                          </Typography>
                                        );
                                      }
                                      const option = calendarOptions.find((item) => item.id === value);
                                      return (
                                        option?.name ||
                                        calendar.googleCalendarName ||
                                        value
                                      );
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>
                                        {t(
                                          'hostGoogleCalendar.noCalendarSelected',
                                          'No calendar selected',
                                        )}
                                      </em>
                                    </MenuItem>
                                    {hasOrphanSelection && (
                                      <MenuItem value={selectedId}>
                                        {calendar.googleCalendarName || selectedId}
                                      </MenuItem>
                                    )}
                                    {calendarOptions.map((option) => (
                                      <MenuItem key={option.id} value={option.id}>
                                        {option.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                              {isSaving && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                                  <CircularProgress size={14} />
                                  <Typography variant="caption" color="text.secondary">
                                    {t('hostGoogleCalendar.updatingLink', 'Updating…')}
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={
                                  calendar.status === 'BROKEN' ? (
                                    <ErrorOutlineIcon />
                                  ) : needsAttention ? (
                                    <WarningAmberIcon />
                                  ) : undefined
                                }
                                label={renderStatusLabel(calendar.status)}
                                color={statusChipColor(calendar.status)}
                                variant={calendar.status === 'ACTIVE' ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {formatLastSyncedAt(calendar.lastSyncedAt)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}
        </Card>
      </Box>

      <Dialog open={disconnectDialogOpen} onClose={() => !disconnecting && setDisconnectDialogOpen(false)}>
        <DialogTitle>{t('hostGoogleCalendar.disconnectTitle', 'Disconnect Google Calendar?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'hostGoogleCalendar.disconnectConfirm',
              'This disconnects the organization Google account. Room and tutor calendar links may stop syncing until you reconnect.',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)} disabled={disconnecting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleDisconnect()} disabled={disconnecting}>
            {disconnecting
              ? t('hostGoogleCalendar.disconnecting', 'Disconnecting...')
              : t('hostGoogleCalendar.disconnect', 'Disconnect')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default HostGoogleCalendarPage;
